import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { port, GlobalEnquiryContext } from '../App';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ChevronRight, ArrowRight, CheckCircle2 } from 'lucide-react';

import ExecutionFramework from "../Component/Services/ExecutionFramework";
import ServiceHero from "../Component/Services/ServiceHero";
import ServiceDifferentiators from "../Component/Services/ServiceDifferentiators";
import ServiceFinalCTA from "../Component/Services/ServiceFinalCTA";

import { subServicesData } from '../Component/SubServicesData';

const SubServicePage = () => {
    const { sid, subsid } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    const { openEnquiry } = useContext(GlobalEnquiryContext);

    useEffect(() => {
        window.scrollTo(0, 0);

        if (!subsid) {
            setLoading(false);
            return;
        }

        const normalizedSubsid = subsid.toLowerCase();
        const staticData = subServicesData[normalizedSubsid];

        if (staticData) {
            setContent(staticData);
            setLoading(false);
        } else {
            // Fallback to try loading dynamically or show not found
            setLoading(false);
        }
    }, [subsid]);



    const handleSecondaryCTAClick = () => {
        const el = document.getElementById('features-section');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <div className="bg-slate-50 min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!content) {
        return (
            <div className="bg-slate-50 min-h-screen flex flex-col items-center justify-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Sub-Service Not Found</h2>
                <button onClick={() => navigate(`/service/${sid || ''}`)} className="px-6 py-3 bg-blue-600 text-white rounded-lg">Return to Service Page</button>
            </div>
        );
    }

    const heroData = {
        title: content.hero_title || content.title,
        subtitle: content.hero_subtitle,
        description: content.hero_description,
        primaryCTA: content.hero_primary_cta || "Get Started Now",
        secondaryCTA: content.hero_secondary_cta || "Contact Us",
        heroImage: content.hero_image
    };

    return (
        <div className="bg-slate-50 min-h-screen">
            <Helmet>
                <title>{content.meta_title || `${content.title} | Merida`}</title>
                <meta name="description" content={content.meta_description || ""} />
                <link rel="canonical" href={`https://meridahr.com/service/${sid}/${content.slug}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": content.title,
                        "provider": {
                            "@type": "LocalBusiness",
                            "name": "Merida HR Consulting",
                            "address": {
                                "@type": "PostalAddress",
                                "streetAddress": "1st Floor, 334/28, 14th Cross Rd, 2nd Block, Jayanagar",
                                "addressLocality": "Bengaluru",
                                "addressRegion": "Karnataka",
                                "postalCode": "560011",
                                "addressCountry": "IN"
                            }
                        },
                        "areaServed": "IN",
                        "description": content.meta_description || content.hero_description
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://www.meridahr.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Services",
                                "item": "https://www.meridahr.com/service"
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": sid ? (sid.charAt(0).toUpperCase() + sid.slice(1)).replace(/-/g, ' ') : "Service Category",
                                "item": `https://www.meridahr.com/service/${sid}`
                            },
                            {
                                "@type": "ListItem",
                                "position": 4,
                                "name": content.title,
                                "item": `https://www.meridahr.com/service/${sid}/${subsid}`
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <HomeNavBar />

            <ServiceHero
                data={heroData}
                onPrimaryClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                onSecondaryClick={handleSecondaryCTAClick}
            />

            {/* FEATURES GRID SECTION */}
            {content.features_list && content.features_list.length > 0 && (
                <section id="features-section" className="py-24 bg-white relative overflow-hidden">
                    <div className="container mx-auto px-6 relative z-10">
                        <div className=" mx-auto text-center mb-16">
                            <span className="text-blue-600 font-black tracking-[0.3em] text-[10px] uppercase block mb-3">Core Solutions</span>
                            <h2 className="text-3xl md:text-5xl font-bold text-[#01014C] syne mb-4">{content.features_title}</h2>
                            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                                Discover the specialized capabilities and technical workflows tailored to address your specific operational requirements.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {content.features_list.map((feat, i) => (
                                <div key={i} className="p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] hover:bg-white hover:shadow-2xl transition-all duration-300">
                                    <div className="w-12 h-12 bg-blue-100/50 text-blue-600 flex items-center justify-center rounded-2xl mb-6 font-black text-lg">
                                        0{i + 1}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feat.title}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed">{feat.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Why Choose Us */}
            {content.differentiators_list && content.differentiators_list.length > 0 && (
                <ServiceDifferentiators
                    title={content.differentiators_title}
                    differentiators={content.differentiators_list}
                />
            )}

            {/* Execution Steps */}
            {content.process_list && content.process_list.length > 0 && (
                <ExecutionFramework steps={content.process_list.map(s => ({
                    title: s.title,
                    description: s.description
                }))} />
            )}

            <ServiceFinalCTA
                title={content.final_cta_title}
                desc={content.final_cta_description}
                p1={content.final_cta_primary}
                p2={content.final_cta_secondary}
                onPrimaryClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
                onSecondaryClick={() => openEnquiry(content.enquiry_title || `Connect With Our ${content.title} Specialists`, content.enquiry_fields || [])}
            />
        </div>
    );
};

export default SubServicePage;
